import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';

import { vehicleService } from '../../../application/VehicleService';
import useErrorMiddleware from '../../../hooks/errorMiddleware/useErrorMiddleware';

const LONG_POLLING_INTERVAL = 3000;

interface UseRetrieveVehicleHasSessionProps {
  vehicleId: string;
}

type UseRetrieveVehicleHasSessionErrorResponse = AxiosError<{ message: string; messageId: string }, any> | null;

type UseRetrieveVehicleHasSessionResponseProps = {
  error: UseRetrieveVehicleHasSessionErrorResponse;
  success: boolean;
  isLoading: boolean;
  run: Function;
};

export const useRetrieveVehicleHasSession = ({
  vehicleId,
}: UseRetrieveVehicleHasSessionProps): UseRetrieveVehicleHasSessionResponseProps => {
  const [success, setSuccess] = useState<boolean>(false);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [refetchInterval, setRefetchInterval] = useState<number | false>(LONG_POLLING_INTERVAL);
  const { sessionExpiredLogout } = useErrorMiddleware();

  const { isLoading, isRefetching, error } = useQuery(
    [`vehicle_has_session`, vehicleId],
    () => vehicleService.retrieveHasSession({ vehicleId }),
    {
      enabled,
      refetchInterval,
      refetchOnWindowFocus: false,
      onSuccess: (hasSession) => {
        if (hasSession) {
          setRefetchInterval(false);
          setSuccess(true);
        }
      },
      onError: (err: UseRetrieveVehicleHasSessionErrorResponse) => {
        console.info(`Retrieve Vehicle Has Session Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );

  return {
    error,
    success,
    isLoading: isLoading || isRefetching,
    run: () => {
      setEnabled(true);
    },
  };
};
